'use client';

import { use } from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'use-intl';

import { SurveyThemeContext } from '@/shared-utilities';

import { useSurveyTimer } from './shared-survey-progress-ring.hooks';
import type { SharedSurveyProgressRingProps } from './shared-survey-progress-ring.types';

const RingContainer = styled.div<{ size: number }>`
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
`;

const TimeText = styled.p<{ color?: string }>`
  ${(props) => props.color && `color: ${props.color};`}
`;

const DEFAULT_SIZE = 48;
const DEFAULT_STROKE_WIDTH = 3;

export default function SharedSurveyProgressRing({
  estimatedTotalTimeInSeconds,
  progress,
  size = DEFAULT_SIZE,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  backgroundColor = '#F2F2F7',
  progressColor,
  textColor = '#6B7280',
  textClassName,
  textNumberClassName,
  textSymbolClassName,
  className = '',
}: SharedSurveyProgressRingProps) {
  const { styles } = use(SurveyThemeContext);

  const t = useTranslations('Time');
  const { remainingMinutes } = useSurveyTimer({
    totalTimeInSeconds: estimatedTotalTimeInSeconds,
    progress,
  });

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  const stroke = progressColor || styles['--brand-primary'] || '#272753';

  return (
    <RingContainer className={`relative shrink-0 ${className}`} size={size}>
      {/* Background circle */}
      <svg className='absolute inset-0 size-full -rotate-90'>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap='round'
        />
      </svg>
      {/* Time text */}
      <div className='absolute inset-0 flex items-center justify-center'>
        <TimeText
          className={`text-center leading-none ${textClassName || ''}`}
          color={!textClassName ? textColor : undefined}
        >
          <span className={textNumberClassName || 'text-sm font-semibold'}>
            {remainingMinutes}
          </span>
          <span className={textSymbolClassName || 'text-[8px] font-normal'}>
            {t('minutesShort')}
          </span>
        </TimeText>
      </div>
    </RingContainer>
  );
}
